import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUpIcon, TrendingDownIcon, DollarSignIcon, PackageIcon, UsersIcon, PlusIcon, PhoneIcon, AreaChartIcon, CheckCircleIcon, Clock8Icon, XCircleIcon, ShieldIcon, GavelIcon } from 'lucide-react';
import LiveMarketTicker from '../components/LiveMarketTicker';
import RechartsLineChart from '../components/charts/RechartsLineChart';
import RechartsBarChart from '../components/charts/RechartsBarChart';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
interface MetricCardProps {
  title: string;
  value: string;
  change: string;
  isPositive: boolean;
  icon: React.ReactNode;
}
const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  isPositive,
  icon
}) => {
  return <Card data-id="element-538">
      <CardContent className="p-6" data-id="element-539">
        <div className="flex items-center justify-between mb-4" data-id="element-540">
          <span className="text-sm text-muted-foreground" data-id="element-541">{title}</span>
          <div className="p-2 rounded-md bg-primary/10 text-primary" data-id="element-542">
            {icon}
          </div>
        </div>
        <div className="flex items-end justify-between" data-id="element-543">
          <span className="text-2xl font-bold" data-id="element-544">{value}</span>
          <span className={`text-sm font-medium flex items-center ${isPositive ? 'text-green-500' : 'text-red-500'}`} data-id="element-545">
            {isPositive ? <TrendingUpIcon className="h-4 w-4 mr-1" data-id="element-546" /> : <TrendingDownIcon className="h-4 w-4 mr-1" data-id="element-547" />}
            {change}
          </span>
        </div>
      </CardContent>
    </Card>;
};
const revenueData = [{
  date: 'Jan',
  value: 12000
}, {
  date: 'Feb',
  value: 14000
}, {
  date: 'Mar',
  value: 11000
}, {
  date: 'Apr',
  value: 18000
}, {
  date: 'May',
  value: 16000
}, {
  date: 'Jun',
  value: 19000
}, {
  date: 'Jul',
  value: 22000
}];
const inventoryData = [{
  label: 'iPhone 13',
  value: 45
}, {
  label: 'iPhone 12',
  value: 32
}, {
  label: 'Galaxy S22',
  value: 28
}, {
  label: 'Pixel 6',
  value: 18
}, {
  label: 'iPhone 11',
  value: 24
}, {
  label: 'OnePlus 10',
  value: 15
}];
const tradeInRequests = [{
  id: 1,
  device: 'iPhone 13 Pro',
  customer: 'Alex Johnson',
  value: '$520',
  status: 'pending',
  time: '2 hours ago'
}, {
  id: 2,
  device: 'Samsung S21',
  customer: 'Maria Garcia',
  value: '$410',
  status: 'completed',
  time: '3 hours ago'
}, {
  id: 3,
  device: 'Google Pixel 6',
  customer: 'James Wilson',
  value: '$380',
  status: 'rejected',
  time: '5 hours ago'
}, {
  id: 4,
  device: 'iPhone 12',
  customer: 'Sarah Miller',
  value: '$350',
  status: 'completed',
  time: '1 day ago'
}];
const activeSyndicates = [{
  id: 'syn-2023-001',
  name: 'Premium iPhone Collective',
  targetCapital: 100000,
  raisedCapital: 90000,
  members: 5,
  endDate: '2023-07-25',
  progress: 90,
  type: 'auction'
}, {
  id: 'syn-2023-002',
  name: 'Samsung Galaxy Traders',
  targetCapital: 75000,
  raisedCapital: 75000,
  members: 3,
  endDate: '2023-06-30',
  progress: 100,
  type: 'bulk'
}];
const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  return <div className="space-y-6" data-id="element-548">
      <div className="flex items-center justify-between" data-id="element-549">
        <h1 className="text-2xl font-bold" data-id="element-550">Dashboard</h1>
        <Button className="font-medium" data-id="element-551">
          <PlusIcon className="mr-2 h-4 w-4" data-id="element-552" />
          New Trade-In
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-553">
        <MetricCard title="Market Value" value="$45,231" change="+20.1%" isPositive={true} icon={<DollarSignIcon className="h-4 w-4" data-id="element-555" />} data-id="element-554" />
        <MetricCard title="Total Inventory" value="123" change="-2.3%" isPositive={false} icon={<PackageIcon className="h-4 w-4" data-id="element-557" />} data-id="element-556" />
        <MetricCard title="Revenue" value="$12,234" change="+12.4%" isPositive={true} icon={<DollarSignIcon className="h-4 w-4" data-id="element-559" />} data-id="element-558" />
        <MetricCard title="Active Traders" value="1,234" change="+4.3%" isPositive={true} icon={<UsersIcon className="h-4 w-4" data-id="element-561" />} data-id="element-560" />
      </div>
      <LiveMarketTicker data-id="element-562" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-563">
        <Card data-id="element-564">
          <CardHeader data-id="element-565">
            <div className="flex items-center justify-between" data-id="element-566">
              <div data-id="element-567">
                <CardTitle data-id="element-568">Revenue Trends</CardTitle>
                <CardDescription data-id="element-569">Monthly revenue data</CardDescription>
              </div>
              <AreaChartIcon className="h-4 w-4 text-muted-foreground" data-id="element-570" />
            </div>
          </CardHeader>
          <CardContent data-id="element-571">
            <RechartsLineChart data={revenueData} height={200} color="#3b82f6" yAxisFormatter={value => `$${value}`} tooltipFormatter={value => [`$${value}`, 'Revenue']} data-id="element-572" />
          </CardContent>
          <CardFooter className="border-t pt-4" data-id="element-573">
            <div className="flex justify-between w-full text-sm" data-id="element-574">
              <div className="text-muted-foreground" data-id="element-575">Total Revenue (YTD)</div>
              <div className="font-medium" data-id="element-576">$112,000</div>
            </div>
          </CardFooter>
        </Card>
        <Card data-id="element-577">
          <CardHeader data-id="element-578">
            <div className="flex items-center justify-between" data-id="element-579">
              <div data-id="element-580">
                <CardTitle data-id="element-581">Inventory Breakdown</CardTitle>
                <CardDescription data-id="element-582">Phones in stock by model</CardDescription>
              </div>
              <PackageIcon className="h-4 w-4 text-muted-foreground" data-id="element-583" />
            </div>
          </CardHeader>
          <CardContent data-id="element-584">
            <RechartsBarChart data={inventoryData} height={200} barColor="#3b82f6" yAxisFormatter={value => `${value}`} tooltipFormatter={value => [`${value}`, 'Units']} data-id="element-585" />
          </CardContent>
          <CardFooter className="border-t pt-4" data-id="element-586">
            <div className="flex justify-between w-full text-sm" data-id="element-587">
              <div className="text-muted-foreground" data-id="element-588">Total Devices</div>
              <div className="font-medium" data-id="element-589">162</div>
            </div>
          </CardFooter>
        </Card>
      </div>
      <Card data-id="element-590">
        <CardHeader data-id="element-591">
          <div className="flex items-center justify-between" data-id="element-592">
            <div data-id="element-593">
              <CardTitle data-id="element-594">Recent Trade-In Requests</CardTitle>
              <CardDescription data-id="element-595">
                Latest device trade-in submissions
              </CardDescription>
            </div>
            <PhoneIcon className="h-4 w-4 text-muted-foreground" data-id="element-596" />
          </div>
        </CardHeader>
        <CardContent data-id="element-597">
          <div className="overflow-x-auto" data-id="element-598">
            <table className="w-full" data-id="element-599">
              <thead data-id="element-600">
                <tr className="border-b" data-id="element-601">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-602">
                    Device
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-603">
                    Customer
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-604">
                    Value
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-605">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-606">
                    Time
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-607">
                {tradeInRequests.map(request => <tr key={request.id} className="border-b hover:bg-muted/50" data-id="element-608">
                    <td className="py-3 px-4" data-id="element-609">{request.device}</td>
                    <td className="py-3 px-4" data-id="element-610">{request.customer}</td>
                    <td className="py-3 px-4 font-medium" data-id="element-611">{request.value}</td>
                    <td className="py-3 px-4" data-id="element-612">
                      {request.status === 'pending' && <Badge variant="warning" className="flex items-center w-fit" data-id="element-613">
                          <Clock8Icon className="mr-1 h-3 w-3" data-id="element-614" />
                          Pending
                        </Badge>}
                      {request.status === 'completed' && <Badge variant="success" className="flex items-center w-fit" data-id="element-615">
                          <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-616" />
                          Completed
                        </Badge>}
                      {request.status === 'rejected' && <Badge variant="destructive" className="flex items-center w-fit" data-id="element-617">
                          <XCircleIcon className="mr-1 h-3 w-3" data-id="element-618" />
                          Rejected
                        </Badge>}
                    </td>
                    <td className="py-3 px-4 text-muted-foreground" data-id="element-619">
                      {request.time}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </CardContent>
        <CardFooter className="border-t" data-id="element-620">
          <Button variant="outline" size="sm" className="ml-auto" data-id="element-621">
            View All Requests
          </Button>
        </CardFooter>
      </Card>
      <Card data-id="element-622">
        <CardHeader data-id="element-623">
          <div className="flex items-center justify-between" data-id="element-624">
            <div data-id="element-625">
              <CardTitle data-id="element-626">Risk Assessment Overview</CardTitle>
              <CardDescription data-id="element-627">Recent device risk evaluations</CardDescription>
            </div>
            <ShieldIcon className="h-4 w-4 text-muted-foreground" data-id="element-628" />
          </div>
        </CardHeader>
        <CardContent data-id="element-629">
          <div className="space-y-4" data-id="element-630">
            <div className="overflow-x-auto" data-id="element-631">
              <table className="w-full" data-id="element-632">
                <thead data-id="element-633">
                  <tr className="border-b" data-id="element-634">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-635">
                      Device
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-636">
                      IMEI
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-637">
                      Risk Score
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-638">
                      Highest Risk Factor
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-639">
                      Status
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground" data-id="element-640">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-641">
                  <tr className="border-b hover:bg-muted/50" data-id="element-642">
                    <td className="py-3 px-4 font-medium" data-id="element-643">iPhone 13 Pro</td>
                    <td className="py-3 px-4" data-id="element-644">358673102468313</td>
                    <td className="py-3 px-4" data-id="element-645">
                      <Badge variant="warning" className="flex items-center w-fit" data-id="element-646">
                        47.25
                      </Badge>
                    </td>
                    <td className="py-3 px-4" data-id="element-647">Transaction Risk</td>
                    <td className="py-3 px-4" data-id="element-648">
                      <Badge variant="warning" className="flex items-center w-fit" data-id="element-649">
                        <Clock8Icon className="mr-1 h-3 w-3" data-id="element-650" />
                        Pending Verification
                      </Badge>
                    </td>
                    <td className="py-3 px-4" data-id="element-651">
                      <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => navigate('/risk-assessment')} data-id="element-652">
                        View Details
                      </Button>
                    </td>
                  </tr>
                  <tr className="border-b hover:bg-muted/50" data-id="element-653">
                    <td className="py-3 px-4 font-medium" data-id="element-654">Samsung S22 Ultra</td>
                    <td className="py-3 px-4" data-id="element-655">354751092639745</td>
                    <td className="py-3 px-4" data-id="element-656">
                      <Badge variant="success" className="flex items-center w-fit" data-id="element-657">
                        28.40
                      </Badge>
                    </td>
                    <td className="py-3 px-4" data-id="element-658">Device Verification</td>
                    <td className="py-3 px-4" data-id="element-659">
                      <Badge variant="success" className="flex items-center w-fit" data-id="element-660">
                        <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-661" />
                        Approved
                      </Badge>
                    </td>
                    <td className="py-3 px-4" data-id="element-662">
                      <Button variant="ghost" size="sm" className="h-8 px-2" data-id="element-663">
                        View Details
                      </Button>
                    </td>
                  </tr>
                  <tr className="border-b hover:bg-muted/50" data-id="element-664">
                    <td className="py-3 px-4 font-medium" data-id="element-665">Google Pixel 6</td>
                    <td className="py-3 px-4" data-id="element-666">351698074512369</td>
                    <td className="py-3 px-4" data-id="element-667">
                      <Badge variant="destructive" className="flex items-center w-fit" data-id="element-668">
                        72.15
                      </Badge>
                    </td>
                    <td className="py-3 px-4" data-id="element-669">Supplier Risk</td>
                    <td className="py-3 px-4" data-id="element-670">
                      <Badge variant="destructive" className="flex items-center w-fit" data-id="element-671">
                        <XCircleIcon className="mr-1 h-3 w-3" data-id="element-672" />
                        Rejected
                      </Badge>
                    </td>
                    <td className="py-3 px-4" data-id="element-673">
                      <Button variant="ghost" size="sm" className="h-8 px-2" data-id="element-674">
                        View Details
                      </Button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
        <CardFooter className="border-t" data-id="element-675">
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => navigate('/risk-assessment')} data-id="element-676">
            View All Assessments
          </Button>
        </CardFooter>
      </Card>
      <Card data-id="element-677">
        <CardHeader data-id="element-678">
          <div className="flex items-center justify-between" data-id="element-679">
            <div data-id="element-680">
              <CardTitle data-id="element-681">Active Syndicates</CardTitle>
              <CardDescription data-id="element-682">Your group buying opportunities</CardDescription>
            </div>
            <div className="flex items-center space-x-2" data-id="element-683">
              <Button variant="outline" size="sm" onClick={() => navigate('/syndicate-hub')} data-id="element-684">
                <UsersIcon className="mr-2 h-4 w-4" data-id="element-685" />
                Syndicate Hub
              </Button>
              <Button size="sm" onClick={() => navigate('/syndicate-creation')} data-id="element-686">
                <PlusIcon className="mr-2 h-4 w-4" data-id="element-687" />
                Create Syndicate
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent data-id="element-688">
          <div className="space-y-4" data-id="element-689">
            {activeSyndicates.map(syndicate => <Card key={syndicate.id} className="overflow-hidden" data-id="element-690">
                <div className="flex flex-col md:flex-row" data-id="element-691">
                  <div className="p-4 md:w-2/3" data-id="element-692">
                    <div className="flex items-start justify-between" data-id="element-693">
                      <div data-id="element-694">
                        <div className="flex items-center" data-id="element-695">
                          <h3 className="font-medium" data-id="element-696">{syndicate.name}</h3>
                          <Badge variant={syndicate.progress === 100 ? 'success' : 'outline'} className="ml-2" data-id="element-697">
                            {syndicate.progress === 100 ? 'Active' : 'Funding'}
                          </Badge>
                          <Badge variant="outline" className="ml-2 capitalize" data-id="element-698">
                            {syndicate.type}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center text-sm" data-id="element-699">
                        <Clock8Icon className="h-4 w-4 mr-1 text-yellow-500" data-id="element-700" />
                        <span data-id="element-701">{syndicate.endDate}</span>
                      </div>
                    </div>
                    <div className="mt-4" data-id="element-702">
                      <div className="flex justify-between text-sm mb-1" data-id="element-703">
                        <span className="text-muted-foreground" data-id="element-704">
                          Progress ({syndicate.progress}%)
                        </span>
                        <span data-id="element-705">
                          ${syndicate.raisedCapital.toLocaleString()} of $
                          {syndicate.targetCapital.toLocaleString()}
                        </span>
                      </div>
                      <Progress value={syndicate.progress} className="h-2" data-id="element-706" />
                    </div>
                    <div className="mt-4 flex items-center space-x-4 text-sm" data-id="element-707">
                      <div className="flex items-center" data-id="element-708">
                        <UsersIcon className="h-4 w-4 mr-1 text-muted-foreground" data-id="element-709" />
                        <span data-id="element-710">{syndicate.members} members</span>
                      </div>
                      <div className="flex items-center" data-id="element-711">
                        <div className="h-4 w-4 mr-1 text-muted-foreground" data-id="element-712" />
                        <span data-id="element-713">Your share: 25%</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-muted/30 border-t md:border-t-0 md:border-l md:w-1/3" data-id="element-714">
                    <div className="space-y-4" data-id="element-715">
                      <div data-id="element-716">
                        <p className="text-sm text-muted-foreground" data-id="element-717">
                          Your Contribution
                        </p>
                        <p className="text-xl font-bold" data-id="element-718">$25,000</p>
                      </div>
                      <div className="space-y-2" data-id="element-719">
                        <Button className="w-full" onClick={() => navigate(`/syndicate-detail/${syndicate.id}`)} data-id="element-720">
                          View Details
                        </Button>
                        {syndicate.progress < 100 && <Button variant="outline" className="w-full" onClick={() => navigate(`/syndicate-contribution/${syndicate.id}`)} data-id="element-721">
                            <DollarSignIcon className="mr-2 h-4 w-4" data-id="element-722" />
                            Contribute
                          </Button>}
                        {syndicate.progress === 100 && <Button variant="outline" className="w-full" onClick={() => navigate(`/syndicate-distribution/${syndicate.id}`)} data-id="element-723">
                            <PackageIcon className="mr-2 h-4 w-4" data-id="element-724" />
                            View Distribution
                          </Button>}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>)}
          </div>
        </CardContent>
        <CardFooter className="border-t" data-id="element-725">
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => navigate('/syndicate-hub')} data-id="element-726">
            View All Syndicates
          </Button>
        </CardFooter>
      </Card>
    </div>;
};
export default Dashboard;